/**
 * Percussion voices. Each is a one-shot graph scheduled at an absolute
 * AudioContext time and left to tear itself down when it finishes.
 */

import type { VoiceBus } from './graph.ts';

export function kick({ ctx, music, noise }: VoiceBus, when: number, amp: number): void {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(148, when);
  o.frequency.exponentialRampToValueAtTime(44, when + 0.11);
  g.gain.setValueAtTime(amp, when);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.34);
  o.connect(g).connect(music);
  o.start(when);
  o.stop(when + 0.36);

  const c = ctx.createBufferSource();
  const cf = ctx.createBiquadFilter();
  const cg = ctx.createGain();
  c.buffer = noise;
  c.playbackRate.value = 1.6;
  cf.type = 'lowpass';
  cf.frequency.value = 2600;
  cg.gain.setValueAtTime(amp * 0.22, when);
  cg.gain.exponentialRampToValueAtTime(0.0001, when + 0.03);
  c.connect(cf).connect(cg).connect(music);
  c.start(when);
  c.stop(when + 0.05);
}

export function snare({ ctx, music, verb, noise }: VoiceBus, when: number, amp: number): void {
  const n = ctx.createBufferSource();
  const f = ctx.createBiquadFilter();
  const g = ctx.createGain();
  n.buffer = noise;
  n.playbackRate.value = 0.9 + Math.random() * 0.2;
  f.type = 'bandpass';
  f.frequency.value = 1750;
  f.Q.value = 0.8;
  g.gain.setValueAtTime(amp, when);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.19);
  n.connect(f).connect(g);
  g.connect(music);
  g.connect(verb);
  n.start(when);
  n.stop(when + 0.22);

  const o = ctx.createOscillator();
  const og = ctx.createGain();
  o.type = 'triangle';
  o.frequency.setValueAtTime(196, when);
  o.frequency.exponentialRampToValueAtTime(135, when + 0.09);
  og.gain.setValueAtTime(amp * 0.4, when);
  og.gain.exponentialRampToValueAtTime(0.0001, when + 0.1);
  o.connect(og).connect(music);
  o.start(when);
  o.stop(when + 0.12);
}

export function hat(
  { ctx, music, noise }: VoiceBus,
  when: number,
  amp: number,
  open: boolean,
): void {
  const n = ctx.createBufferSource();
  const f = ctx.createBiquadFilter();
  const g = ctx.createGain();
  n.buffer = noise;
  n.playbackRate.value = 1.7;
  f.type = 'highpass';
  f.frequency.value = open ? 6200 : 8200;
  const dur = open ? 0.22 : 0.045;
  g.gain.setValueAtTime(amp, when);
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  n.connect(f).connect(g).connect(music);
  n.start(when);
  n.stop(when + dur + 0.02);
}
